import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { useChips } from '../lib/useChips.js';
import { count, exact } from '../lib/format.js';
import { ChipBar, FilterPills, Screen } from '../components/Screen.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  HoldButton,
  Identity,
  Note,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { IssueCredential } from '../modals/IssueCredential.js';
import { CredentialRecord } from './CredentialRecord.js';

/**
 * Every credential on the platform.
 *
 * ── This screen is not built on the plugins' own listings ────────────
 *
 * `/oauth2/get-clients` returns clients whose `userId` is the caller's, and
 * `/api-key/list` returns the caller's own keys. Both are correct for a user
 * managing their own credentials, and both are the wrong question here — in the
 * way that does not announce itself. The overview counts every row and says
 * "7 service credentials"; a screen on the self-scoped endpoint lists the two
 * the signed-in admin happens to own; nothing errors. A short list is
 * indistinguishable from a complete one.
 *
 * So the auth service grew `/admin/credentials`, which reads the same tables the
 * overview counts. The two cannot disagree.
 *
 * ── ISSUING IS HERE NOW, and it did not used to be ───────────────────
 *
 * This said: a key is only usable by whoever receives the secret, so minting
 * stays self-service at `/api-key/create` and this screen never sees key
 * material. The reasoning was sound and the premise stopped holding.
 *
 * `permissions` is a SERVER-ONLY property on that endpoint — the plugin refuses
 * it from any request carrying headers — so a self-service key can never hold
 * one, and a key with no permissions is refused by every route that demands one.
 * There is no path left where the holder mints their own usable credential.
 *
 * The original concern is answered by shape instead: the value is shown once,
 * never stored here, and never re-fetchable. This screen holds no key material
 * once the modal closes. See modals/IssueCredential.tsx.
 *
 * ── What is deliberately not here ────────────────────────────────────
 *
 * REGISTERING AN OAUTH CLIENT. Dynamic registration is off, and the first-party
 * clients live in the auth service's `lib/clients.ts` so the trusted set and the
 * registered set cannot drift. Applications ARE registered from the portal; an
 * OAuth client is a different thing, and making it a portal action would trade a
 * property that stops a first-party app prompting its own users for consent
 * against convenience nobody has asked for.
 */
export function Developer({ id }: { id?: string }) {
  /* A credential is a record at `#/developer/:id`, like every other record
     (App.tsx). Split here so neither screen's hooks depend on the other's. */
  return id ? <CredentialRecord id={id} /> : <CredentialList />;
}

function CredentialList() {
  /**
   * TABS, not two stacked panels.
   *
   * The two halves answer different questions — "which applications can ask for
   * a token" and "which machines hold a key" — and stacking them meant the
   * second was below a table of unknown length. One search box above both,
   * because the chips apply to both: the server filters each half by the same
   * terms, so a tab switch never disagrees with the chips on screen.
   */
  const [tab, setTab] = useState<'keys' | 'clients'>('keys');
  const [issuing, setIssuing] = useState(false);
  const search = useChips();
  const { state, reload } = useAsync(() => adminApi.credentials({ terms: search.active }), [
    search.key,
  ]);
  /**
   * The application list, for the issue form's picker.
   *
   * Loaded here rather than inside the modal so opening it is instant and an
   * empty picker is visible as an empty picker. `appSlug` is what scopes the
   * credential, so a free-text field would let a typo mint a key scoped to an
   * app that does not exist — which fails closed, and fails in a way nobody can
   * read off the key afterwards.
   */
  const apps = useAsync(() => adminApi.applications(), []);
  const action = useAction();

  return (
    <Screen
      filters={
        <FilterPills
          label="Credential type"
          current={tab}
          onSelect={setTab}
          options={[
            { value: 'keys' as const, label: 'Service credentials' },
            { value: 'clients' as const, label: 'OAuth clients' },
          ]}
        />
      }
      chips={
        <ChipBar
          search={search}
          placeholder="Search a client id, key name or prefix…"
          appliedByServer={state.status === 'ready' ? state.data.terms : undefined}
        />
      }
    >
      <AsyncBoundary state={state}>
        {(d) => (
          <>
            {tab === 'clients' ? (
            <Panel title="OAuth clients">
              {d.oauthClients.length === 0 ? (
                <EmptyState
                  title={
                    search.active.length > 0
                      ? 'No clients match your search'
                      : 'No clients registered'
                  }
                  suggestion={
                    search.active.length > 0
                      ? 'Try the client id, or a word from its name.'
                      : 'They are seeded from the auth service’s lib/clients.ts. An empty table usually means the seed has not run against this environment.'
                  }
                />
              ) : (
                <Table columns={['Client', 'Type', 'Consent', 'Redirect URIs', 'Status']}>
                  {d.oauthClients.map((c) => (
                    <tr key={c.id}>
                      <Td>
                        <strong>{c.name ?? c.clientId}</strong>
                        <div style={{ color: C.muted, fontSize: '.8rem', fontFamily: 'ui-monospace, monospace' }}>
                          {c.clientId}
                        </div>
                      </Td>
                      <Td>
                        <span style={{ display: 'inline-flex', gap: '.3rem' }}>
                          {/* `type` is often unset on a seeded row, and a badge
                              reading "unknown" next to one that says "public"
                              looks like missing data rather than an unused
                              field. Omitted when absent. */}
                          {c.type ? <Badge>{c.type}</Badge> : null}
                          {/* A public client holds no secret and proves itself
                              with PKCE. Worth stating on the row: it is the
                              difference between a leaked client id mattering
                              and not. */}
                          {c.public ? <Badge>public</Badge> : <Badge tone="warn">confidential</Badge>}
                        </span>
                      </Td>
                      <Td>
                        {/* From source, not the row. Being in the trusted set is
                            what makes a client skip consent, and it is
                            configuration rather than data. */}
                        {c.trusted ? <Badge tone="good">skipped: first party</Badge> : <Badge>required</Badge>}
                      </Td>
                      <Td mono>
                        {c.redirectUris.length === 0 ? (
                          <Badge tone="danger">none</Badge>
                        ) : (
                          c.redirectUris.map((u) => <div key={u}>{u}</div>)
                        )}
                      </Td>
                      <Td>{c.disabled ? <Badge tone="danger">disabled</Badge> : <Badge tone="good">enabled</Badge>}</Td>
                    </tr>
                  ))}
                </Table>
              )}
              <Note>
                Registered in source and seeded, not from this screen, so a client that is trusted
                cannot drift from a client that exists.
              </Note>
            </Panel>
            ) : (
            <Panel
              title="Service credentials"
              action={
                <Button
                  disabled={apps.state.status !== 'ready'}
                  onClick={() => setIssuing(true)}
                >
                  Issue credential
                </Button>
              }
            >
              <div style={{ padding: '1rem 1.2rem 0' }}>
                <OutcomeNote outcome={action.outcome} />
              </div>

              {d.apiKeys.keys.length === 0 ? (
                <EmptyState
                  title={
                    search.active.length > 0 ? 'No credentials match your search' : 'No API keys issued'
                  }
                  suggestion={
                    search.active.length > 0
                      ? 'Try a key name, or the visible prefix from a log line.'
                      : 'Issue one above. The value is shown once and never stored here.'
                  }
                />
              ) : (
                <Table columns={['Key', 'Owner', 'Service', 'Last used', 'Expires', 'Status', '']}>
                  {d.apiKeys.keys.map((k) => (
                    <tr key={k.id}>
                      <Td>
                        {/* The name opens the credential's own screen, where
                            Edit and Rotate live and every working secret is
                            listed (CredentialRecord.tsx). */}
                        <a href={`#/developer/${encodeURIComponent(k.id)}`} style={rowLink}>
                          {k.name ?? 'unnamed'}
                        </a>
                        {/* The first characters exist so a key can be
                            identified in a log or a paste without being looked
                            up. That is the entire reason to show them. */}
                        <div style={{ color: C.muted, fontSize: '.8rem', fontFamily: 'ui-monospace, monospace' }}>
                          {k.start ?? k.prefix ?? '—'}…
                        </div>
                      </Td>
                      <Td>
                        {k.owner ? (
                          <Identity email={k.owner.email} />
                        ) : (
                          /* An owner id that resolves to nothing is reported
                             rather than blanked — a credential whose holder no
                             longer exists is exactly the one to look at. */
                          <Badge tone="danger">owner missing</Badge>
                        )}
                      </Td>
                      <Td mono>
                        {serviceOf(k.metadata)}
                        {/* Both `warn`, because they are what makes a key
                            unlike an ordinary service credential, and this
                            table is where somebody decides what to revoke. */}
                        {(agentOf(k.metadata) || roleOf(k.metadata)) && (
                          <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap', marginTop: '.3rem' }}>
                            {agentOf(k.metadata) && <Badge tone="warn">agent</Badge>}
                            {roleOf(k.metadata) && <Badge tone="warn">{roleOf(k.metadata)}</Badge>}
                          </div>
                        )}
                        {(k.grants?.length || k.unmanagedPermissions) ? (
                          <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap', marginTop: '.3rem' }}>
                            {k.grants?.map((g) => (
                              <Badge key={g}>{g}</Badge>
                            ))}
                            {/* Shown rather than hidden, because it is the reason
                                the Edit button is missing on this row. */}
                            {k.unmanagedPermissions ? <Badge tone="warn">other permissions</Badge> : null}
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        <When iso={k.lastRequest} />
                        {k.requestCount ? (
                          <div style={{ color: C.muted, fontSize: '.8rem' }}>
                            {count(k.requestCount)} requests
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        {k.expiresAt ? (
                          <When iso={k.expiresAt} />
                        ) : (
                          <span style={{ color: C.muted }}>never</span>
                        )}
                      </Td>
                      <Td>
                        {k.enabled === false ? (
                          <Badge tone="danger">revoked</Badge>
                        ) : (
                          <Badge tone="good">active</Badge>
                        )}
                        {/* Rotated in the last twelve hours: the old secret
                            still works, and this is when it stops. */}
                        {k.enabled !== false && k.previousSecretValidUntil ? (
                          <div style={{ color: C.muted, fontSize: '.8rem', marginTop: '.3rem' }} title={exact(k.previousSecretValidUntil)}>
                            old secret until {exact(k.previousSecretValidUntil)}
                          </div>
                        ) : null}
                      </Td>
                      <Td>
                        {k.enabled === false ? null : (
                          <span style={{ display: 'inline-flex', gap: '.4rem' }}>
                          <HoldButton
                            title={`Hold for a second and a half to revoke ${k.name ?? 'this key'}. It stops working at once.`}
                            disabled={action.running}
                            onHold={() =>
                              void action
                                .run(async () => {
                                  const r = await adminApi.revokeApiKey(k.id);
                                  return r.alreadyRevoked
                                    ? 'Already revoked. Nothing changed.'
                                    : `Revoked ${k.name ?? 'the key'}. It stops working immediately.`;
                                })
                                .then(() => reload())
                            }
                          >
                            Revoke
                          </HoldButton>
                          </span>
                        )}
                      </Td>
                    </tr>
                  ))}
                </Table>
              )}

              <Note>
                Showing {count(d.apiKeys.keys.length)} of {count(d.apiKeys.total)}. Open a
                credential by its name to edit it, rotate it, or see every secret that still works.
                Revoking disables the key rather than deleting it: every secret it has stops working at
                once, and the row stays so the incident can still be answered afterwards. A new secret
                is readable once, at the moment it is created. The service stores only a hash, so a
                lost key is replaced rather than recovered.
              </Note>
            </Panel>
            )}
          </>
        )}
      </AsyncBoundary>

      {issuing && apps.state.status === 'ready' && (
        <IssueCredential
          applications={apps.state.data.applications.filter((a) => a.active)}
          onClose={() => setIssuing(false)}
          onIssued={reload}
        />
      )}

    </Screen>
  );
}

/**
 * Which application a key is scoped to.
 *
 * `appSlug` FIRST, and that is not cosmetic: it is the field the auth service
 * authorises against — it decides whose user base the key can see. `service` is
 * the older key, read as a fallback so rows written before issuing moved here
 * still show something rather than a dash.
 *
 * This column read `service` only, so every credential issued through the new
 * endpoint rendered as "—" while being perfectly well scoped. Anything
 * unrecognised is still a dash rather than `[object Object]`.
 */
export function serviceOf(metadata: Record<string, unknown> | null): string {
  for (const key of ['appSlug', 'service']) {
    const v = metadata?.[key];
    if (typeof v === 'string' && v) return v;
  }
  return '—';
}

/**
 * Whether a credential belongs to an automated agent, and the role it acts
 * with (RidgeWill/platform#31).
 *
 * ── WHY THIS LIST HAS TO SHOW THEM ──────────────────────────────────
 *
 * Both are set at issue time and badged on the one-time reveal, and that was
 * the ONLY place either ever appeared. Once the dialog closed, nothing showed
 * which keys are an agent's or which carry a role, so a credential with the
 * authority of a global admin looked identical to one that can resolve an email
 * address — in the one screen whose job is answering "what exists here, and
 * should any of it be revoked".
 *
 * The marker exists for attribution, and attribution nobody can read is not
 * attribution.
 *
 * ── READ DEFENSIVELY, THE WAY THE SERVICE READS IT ──────────────────
 *
 * Metadata is written by whoever minted the key, so anything that is not the
 * expected shape reads as ABSENT rather than as present. `agent` is strictly
 * the boolean: `"agent": "no"` is a truthy string, and badging that key as an
 * agent would be worse than not badging it at all, because this screen is where
 * somebody decides they have already checked.
 */
export function agentOf(metadata: Record<string, unknown> | null): boolean {
  return metadata?.agent === true;
}

export function roleOf(metadata: Record<string, unknown> | null): string | null {
  const v = metadata?.role;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

const rowLink = { fontWeight: 600, color: C.brand, textDecoration: 'none' };
